import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../AxiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faCog, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../components/modal/modal';
import {useTranslation} from "react-i18next";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";
import {getStateLabels} from "../../components/utils";

// type 5 = Boolean; data_type='boolean' is fallback for legacy records
const isBoolVar = (v) => v?.type === 5 || v?.data_type === 'boolean';
const varTypeLabel = (v) => isBoolVar(v) ? 'Boolean' : 'Numeric';
const varTypeColor = (v) => isBoolVar(v) ? '#7C3AED' : '#2563EB';
const varTypeBg   = (v) => isBoolVar(v) ? '#F5F3FF' : '#EFF6FF';

function VariablePicker({ variables, selected, onSelect }) {
    const {t} = useTranslation();
    const [search, setSearch] = useState('');

    const filtered = variables.filter(v => {
        const q = search.toLowerCase();
        return !q
            || v.variable?.name?.toLowerCase().includes(q)
            || varTypeLabel(v.variable).toLowerCase().includes(q);
    });

    const selectedVar = variables.find(v => String(v.id) === String(selected));

    return (
        <div>
            {/* Current selection chip */}
            <div style={{ marginBottom: '8px' }}>
                {selectedVar ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 10px', borderRadius: '6px', background: '#EEF2FF', border: '1px solid #C7D2FE' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#1E293B' }}>{selectedVar.variable?.name}</span>
                        <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: varTypeBg(selectedVar.variable), color: varTypeColor(selectedVar.variable) }}>
                            {varTypeLabel(selectedVar.variable)}
                        </span>
                        <button onClick={() => onSelect('')} style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: '0', lineHeight: 1 }}>
                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '11px' }} />
                        </button>
                    </div>
                ) : (
                    <span style={{ fontSize: '13px', color: '#94A3B8' }}>{t('maintenance.hours.noneSelected')}</span>
                )}
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '6px' }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px' }} />
                <input
                    placeholder="Search variables..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', boxSizing: 'border-box' }}
                />
            </div>

            {/* Variable list */}
            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                {/* None option */}
                <div
                    role="button"
                    onClick={() => onSelect('')}
                    style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                        background: !selected ? '#EEF2FF' : '#FFF',
                        color: '#64748B', borderBottom: '1px solid #F1F5F9',
                        display: 'flex', alignItems: 'center', gap: '8px',
                    }}
                >
                    <span>{t('maintenance.hours.noneManual')}</span>
                </div>
                {filtered.length === 0 && (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>{t('maintenance.hours.noVariables')}</div>
                )}
                {filtered.map(v => (
                    <div
                        key={v.id}
                        role="button"
                        onClick={() => onSelect(v.id)}
                        style={{
                            padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                            background: String(selected) === String(v.id) ? '#EEF2FF' : '#FFF',
                            borderBottom: '1px solid #F1F5F9',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            transition: 'background 0.1s',
                        }}
                    >
                        <span style={{ fontWeight: String(selected) === String(v.id) ? 600 : 400, color: '#1E293B' }}>
                            {v.variable?.name || 'Variable'}
                            {v._device_name && <span style={{ color: '#94A3B8', fontWeight: 400, marginLeft: '4px' }}>· {v._device_name}</span>}
                        </span>
                        <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: varTypeBg(v.variable), color: varTypeColor(v.variable), flexShrink: 0 }}>
                            {varTypeLabel(v.variable)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function MaintenanceHoursTab({ machine }) {
    const {t} = useTranslation();
    const [config, setConfig] = useState(null);
    const [totalHours, setTotalHours] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(getStoredPageSize);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [configForm, setConfigForm] = useState({});
    const [variables, setVariables] = useState([]);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);
    const project_id = parseInt(localStorage.getItem('project_id'));

    const fetchStatus = () => {
        axiosInstance.get(`machine_hours_config/?machine_id=${machine.id}`).then(res => {
            if (res.data.length > 0) {
                const cfg = res.data[0];
                setConfig(cfg);
                setTotalHours(cfg.total_hours);
                setIsRunning(cfg.is_running);
                setConfigForm({
                    auto_variable: cfg.auto_variable || '',
                    auto_on_threshold: cfg.auto_on_threshold ?? 0.5,
                    auto_on_value: cfg.auto_on_value ?? true,
                    hour_offset: cfg.hour_offset ?? 0,
                });
            } else {
                setConfig(null);
                setTotalHours(0);
                setIsRunning(false);
                setConfigForm({ auto_variable: '', auto_on_threshold: 0.5, auto_on_value: true, hour_offset: 0 });
            }
        });
        axiosInstance.get(`machine_hours_session/?machine_id=${machine.id}`).then(res => {
            setSessions(res.data);
            setPage(1);
        });
    };

    useEffect(() => {
        // Reset state immediately when switching machines
        setConfig(null);
        setTotalHours(0);
        setIsRunning(false);
        setSessions([]);
        setConfigForm({ auto_variable: '', auto_on_threshold: 0.5, auto_on_value: true, hour_offset: 0 });
        fetchStatus();
        pollRef.current = setInterval(fetchStatus, 30000);
        return () => clearInterval(pollRef.current);
    }, [machine.id]);

    const handleToggle = () => {
        if (!config) {
            axiosInstance.post('machine_hours_config/', {
                machine: machine.id,
                project: project_id,
            }).then(() => fetchStatus());
            return;
        }
        setLoading(true);
        axiosInstance.post(`machine_hours_config/${config.id}/toggle/`).then(res => {
            setIsRunning(res.data.is_running);
            setTotalHours(res.data.total_hours);
            fetchStatus();
        }).finally(() => setLoading(false));
    };

    const openConfig = () => {
        // machine is a DeviceUser; Device has device_user FK
        // Each device already embeds its variables via DeviceSerializer.get_variables()
        axiosInstance.get(`device/?machine=${machine.id}`).then(res => {
            const devices = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
            const allVars = devices.flatMap(d =>
                (d.variables || []).map(v => ({ ...v, _device_name: d.name }))
            );
            setVariables(allVars);
        });
        setShowConfigModal(true);
    };

    const saveConfig = () => {
        const selectedVar = variables.find(v => String(v.id) === String(configForm.auto_variable));
        const isBoolean = isBoolVar(selectedVar?.variable);
        const payload = {
            machine: machine.id,
            project: project_id,
            auto_variable: configForm.auto_variable || null,
            auto_on_threshold: isNaN(parseFloat(configForm.auto_on_threshold)) ? 0.5 : parseFloat(configForm.auto_on_threshold),
            auto_on_value: configForm.auto_on_value === true || configForm.auto_on_value === 'true',
            hour_offset: isNaN(parseFloat(configForm.hour_offset)) ? 0 : parseFloat(configForm.hour_offset),
        };
        const req = config
            ? axiosInstance.put(`machine_hours_config/${config.id}/`, payload)
            : axiosInstance.post('machine_hours_config/', payload);
        req.then(() => {
            setShowConfigModal(false);
            fetchStatus();
        });
    };

    const formatHours = (h) => {
        const hours = Math.floor(h);
        const mins = Math.round((h - hours) * 60);
        return `${hours}h ${mins.toString().padStart(2, '0')}m`;
    };

    const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

    const selectedVar = variables.find(v => String(v.id) === String(configForm.auto_variable));
    const isBoolean = selectedVar?.variable?.type === 5;
    // "Encendida cuando el valor es..." se lee mucho mejor con las etiquetas
    // reales de la variable (Abierto/Cerrado, En marcha/Parada) que con True/False.
    const stateLabels = getStateLabels(selectedVar);

    return (
        <div>
            {/* Big hours counter */}
            <div style={{
                background: '#F8FAFC', borderRadius: '16px', padding: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '24px', border: '1px solid #E2E8F0', marginBottom: '24px',
            }}>
                <div>
                    <div style={{ fontSize: '12px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                        {t('maintenance.hours.totalHours')}
                    </div>
                    <div style={{ fontSize: '48px', fontWeight: 700, color: '#1E293B', lineHeight: 1 }}>
                        {formatHours(totalHours)}
                    </div>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px',
                        padding: '4px 12px', borderRadius: '20px',
                        background: isRunning ? '#DCFCE7' : '#F1F5F9',
                        color: isRunning ? '#16A34A' : '#64748B',
                        fontSize: '13px', fontWeight: 500,
                    }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isRunning ? '#16A34A' : '#94A3B8' }} />
                        {isRunning ? t('maintenance.hours.running') : t('maintenance.hours.stopped')}
                    </div>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button
                        onClick={handleToggle}
                        disabled={loading}
                        style={{
                            padding: '12px 24px', borderRadius: '8px', border: 'none',
                            background: isRunning ? '#FEF2F2' : '#4F46E5',
                            color: isRunning ? '#DC2626' : '#FFF',
                            fontWeight: 600, fontSize: '15px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '8px',
                        }}
                    >
                        <FontAwesomeIcon icon={isRunning ? faStop : faPlay} />
                        {isRunning ? t('maintenance.hours.stop') : t('maintenance.hours.start')}
                    </button>
                    <button
                        onClick={openConfig}
                        style={{
                            padding: '12px 16px', borderRadius: '8px', border: '1px solid #E2E8F0',
                            background: '#FFF', color: '#64748B', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px',
                        }}
                    >
                        <FontAwesomeIcon icon={faCog} /> {t('maintenance.createHours')}
                    </button>
                </div>
            </div>

            {/* Sessions history */}
            <div style={{ background: '#FFF', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', fontWeight: 600, color: '#334155' }}>
                    {t('maintenance.hours.sessionHistory')}
                </div>
                {sessions.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#94A3B8' }}>{t('maintenance.hours.noSessions')}</div>
                ) : (() => {
                    const totalPages = Math.ceil(sessions.length / pageSize);
                    const pagedSessions = sessions.slice((page - 1) * pageSize, page * pageSize);
                    return (
                    <div style={{ overflowX: 'auto', padding: '0 16px 16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC' }}>
                                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.hours.colStart')}</th>
                                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.hours.colEnd')}</th>
                                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.hours.colDuration')}</th>
                                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.hours.colSource')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedSessions.map(s => (
                                <tr key={s.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                                    <td style={{ padding: '10px 16px', color: '#334155' }}>{formatDate(s.start_time)}</td>
                                    <td style={{ padding: '10px 16px', color: '#334155' }}>
                                        {s.end_time ? formatDate(s.end_time) : (
                                            <span style={{ color: '#16A34A', fontWeight: 500 }}>{t('maintenance.hours.running')}</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 16px', color: '#334155' }}>{formatHours(s.duration_hours || 0)}</td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <span style={{
                                            padding: '2px 8px', borderRadius: '4px',
                                            background: s.source === 'auto' ? '#EFF6FF' : '#F5F3FF',
                                            color: s.source === 'auto' ? '#3B82F6' : '#7C3AED',
                                            fontSize: '12px', fontWeight: 500,
                                        }}>
                                            {s.source === 'auto' ? t('maintenance.hours.sourceAuto') : t('maintenance.hours.sourceManual')}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ marginTop: '10px' }}>
                        <PaginationControls
                            page={page}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(1); }}
                            totalCount={sessions.length}
                        />
                    </div>
                    </div>
                    );
                })()}
            </div>

            {/* Config modal */}
            <Modal
                show={showConfigModal}
                setter={setShowConfigModal}
                name={t('maintenance.createHours')}
                buttonName={t('common.save')}
                onClick={saveConfig}
            >
                <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '12px' }}>
                    {t('maintenance.hours.configDesc')}
                </p>

                {/* Criteria explanation box */}
                <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: '#64748B', lineHeight: 1.6 }}>
                    <strong style={{ color: '#334155' }}>{t('maintenance.hours.howItWorks')}</strong><br />
                    • <span style={{ color: '#7C3AED', fontWeight: 600 }}>{t('maintenance.hours.booleanVar')}</span>: {t('maintenance.hours.booleanDesc')}<br />
                    • <span style={{ color: '#2563EB', fontWeight: 600 }}>{t('maintenance.hours.numericVar')}</span>: {t('maintenance.hours.numericDesc')}
                </div>

                {/* Initial hours offset */}
                <label style={{ fontSize: '13px', color: '#334155', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                    {t('maintenance.hours.hourOffset')}
                </label>
                <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={configForm.hour_offset ?? 0}
                    onChange={e => setConfigForm(f => ({ ...f, hour_offset: e.target.value }))}
                    style={{ width: '160px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px', marginBottom: '16px' }}
                />

                <label style={{ fontSize: '13px', color: '#334155', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                    {t('maintenance.hours.autoVariable')} <span style={{ color: '#94A3B8', fontWeight: 400 }}>{t('maintenance.hours.optional')}</span>
                </label>
                <VariablePicker
                    variables={variables}
                    selected={configForm.auto_variable}
                    onSelect={(id) => setConfigForm(f => ({ ...f, auto_variable: id }))}
                />

                {configForm.auto_variable && (
                    <div style={{ marginTop: '14px', padding: '12px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                        {isBoolean ? (
                            <div>
                                <label style={{ fontSize: '13px', color: '#334155', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                                    {t('maintenance.hours.onWhenValue')}
                                </label>
                                <div className="d-flex gap-2">
                                    {[['true', stateLabels.trueLabel], ['false', stateLabels.falseLabel]].map(([val, label]) => (
                                        <button
                                            key={val}
                                            onClick={() => setConfigForm(f => ({ ...f, auto_on_value: val === 'true' }))}
                                            style={{
                                                padding: '6px 20px', borderRadius: '6px', border: '1px solid',
                                                borderColor: String(configForm.auto_on_value) === val ? '#4F46E5' : '#E2E8F0',
                                                background: String(configForm.auto_on_value) === val ? '#4F46E5' : '#FFF',
                                                color: String(configForm.auto_on_value) === val ? '#FFF' : '#64748B',
                                                fontWeight: 600, cursor: 'pointer', fontSize: '13px',
                                            }}
                                        >{label}</button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label style={{ fontSize: '13px', color: '#334155', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                                    {t('maintenance.hours.onThreshold')} <strong>&gt;</strong>
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={configForm.auto_on_threshold}
                                    onChange={e => setConfigForm(f => ({ ...f, auto_on_threshold: e.target.value }))}
                                    style={{ width: '120px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px' }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
